import React, { useState, useEffect } from "react";
import axios from "axios";
import { APIConstant } from "services/api-constant";
import { Calendar, Globe, MapPin, Building2, Star } from "lucide-react";
import { useTheme } from "context/ThemeContext";

const typeConfig = (T) => ({
  National:{ color:T.red,   icon:Globe,     bg:`${T.red}1A`  },
  Regional:{ color:T.amber, icon:MapPin,    bg:`${T.amber}1A`   },
  Optional:{ color:T.muted, icon:Star,      bg:`${T.muted}1A` },
  Company: { color:T.cyan,  icon:Building2, bg:`${T.cyan}1A`   },
});
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function EmployeeHolidays() {
  const { T } = useTheme();
  const TYPE_CONFIG = typeConfig(T);
  const token = sessionStorage.getItem("platform_token") || "";
  const ax    = { headers:{ Authorization:`Bearer ${token}` } };
  const year  = new Date().getFullYear();
  const today = new Date().toISOString().split("T")[0];

  const [holidays, setHolidays] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    axios.get(`${APIConstant.holidays}?year=${year}`, ax)
      .then(r => setHolidays(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upcoming = holidays.filter(h => h.date >= today).slice(0,3);
  const byMonth  = MONTHS.reduce((acc, _, mi) => {
    acc[mi] = holidays.filter(h => parseInt(h.date.split("-")[1])-1 === mi);
    return acc;
  }, {});

  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, fontFamily:"'Outfit',sans-serif", overflow:"hidden" };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>
      <div style={{ marginBottom:22 }}>
        <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>ATTENDANCE & LEAVE</div>
        <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>Holiday Calendar {year}</h2>
        <p style={{ fontSize:13, color:T.muted, margin:0 }}>{holidays.length} holidays · Read-only view</p>
      </div>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div style={{ ...card, marginBottom:20, padding:20, borderColor:`${T.amber}26`, background:`${T.amber}0A` }}>
          <div style={{ fontSize:12, fontWeight:700, color:T.amber, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}>Upcoming Holidays</div>
          <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
            {upcoming.map(h => {
              const d   = new Date(h.date);
              const cfg = TYPE_CONFIG[h.type] || TYPE_CONFIG.National;
              return (
                <div key={h.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", background:T.inp_bg, borderRadius:10, border:`1px solid ${cfg.color}40` }}>
                  <div style={{ width:40, height:40, borderRadius:10, background:cfg.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <div style={{ fontSize:16, fontWeight:800, color:cfg.color, lineHeight:1 }}>{d.getDate()}</div>
                    <div style={{ fontSize:9, color:cfg.color, textTransform:"uppercase" }}>{d.toLocaleString("default",{month:"short"})}</div>
                  </div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600 }}>{h.name}</div>
                    <div style={{ fontSize:11.5, color:T.muted }}>{d.toLocaleDateString("en-IN",{weekday:"long"})}</div>
                  </div>
                  <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10, background:cfg.bg, color:cfg.color, fontWeight:600 }}>{h.type}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Month grid */}
      {loading ? <div style={{ padding:40, textAlign:"center", color:T.muted }}>Loading…</div> : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
          {MONTHS.map((month, mi) => {
            const mHols = byMonth[mi] || [];
            const isCurrent = new Date().getMonth() === mi;
            return (
              <div key={month} style={{ ...card, border:isCurrent?`1px solid ${T.cyan}33`:`1px solid ${T.border}` }}>
                <div style={{ padding:"12px 16px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:13, fontWeight:600, color:isCurrent?T.cyan:T.text }}>{month} {year}</span>
                  <span style={{ fontSize:11.5, fontFamily:"JetBrains Mono,monospace", color:T.muted }}>{mHols.length}</span>
                </div>
                <div style={{ padding:"10px 16px" }}>
                  {mHols.length === 0 ? (
                    <div style={{ textAlign:"center", padding:"12px 0", color:T.dim, fontSize:12 }}>No holidays</div>
                  ) : mHols.map(h => {
                    const cfg  = TYPE_CONFIG[h.type] || TYPE_CONFIG.National;
                    const past = h.date < today;
                    const d    = new Date(h.date);
                    return (
                      <div key={h.id} style={{ display:"flex", alignItems:"center", gap:9, padding:"8px 0", borderBottom:`1px solid ${T.border}`, opacity:past?0.5:1 }}>
                        <div style={{ width:28, height:28, borderRadius:7, background:cfg.bg, display:"flex", alignItems:"center", justifyContent:"center", color:cfg.color, flexShrink:0 }}>
                          <cfg.icon size={12}/>
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12, fontWeight:500 }}>{h.name}</div>
                          <div style={{ fontSize:10.5, color:T.dim }}>{d.toLocaleDateString("en-IN",{weekday:"short",day:"2-digit"})}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
